import React, { Suspense, lazy } from "react";
import { LoadingCard } from "@/components/Loading/EnhancedLoadingStates";
import { Button } from "@/components/ui/button";

/**
 * Retry wrapper for dynamic imports – on failure, cache-busts and retries once.
 * If retry also fails, redirects to emergency recovery.
 */
function retryLazy(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch(() => {
      // Retry once after a short delay (network blip / SW updating).
      return new Promise<{ default: React.ComponentType<any> }>((resolve, reject) => {
        setTimeout(() => {
          factory()
            .then(resolve)
            .catch((err) => {
              // Only escalate to hard reset ONCE per tab — main.tsx clears
              // service worker + Cache Storage on the next load. If we've
              // already tried that path this session, surface the error to
              // the boundary instead of looping.
              const RECOVERY_KEY = '__behtari_chunk_recovery';
              let alreadyTried = false;
              try {
                alreadyTried = sessionStorage.getItem(RECOVERY_KEY) === '1';
              } catch { /* storage blocked */ }

              if (!alreadyTried) {
                // Set the sentinel BEFORE navigating so if the navigation
                // fails or is intercepted, the next attempt sees "1" and
                // falls through to the ErrorBoundary rather than looping.
                try { sessionStorage.setItem(RECOVERY_KEY, '1'); } catch { /* noop */ }
                try {
                  window.location.replace(window.location.origin + '?hard_reset=1');
                } catch { /* fall through */ }
              }
              // Always reject so <Suspense> unwinds to the nearest
              // ErrorBoundary — guarantees a recovery UI even if the
              // navigation stalls (offline, blocked, SW race).
              reject(err instanceof Error ? err : new Error('Chunk load failed after retry'));
            });
        }, 1000);
      });
    })
  );
}

// Raw import factories — exposed so prefetch hooks can warm the same chunks
// that retryLazy() will later resolve. Browser de-dupes the import call.
export const importIndex = () => import("@/pages/Index");
export const importPersonaSelection = () => import("@/pages/PersonaSelection");
export const importDashboardPage = () => import("@/pages/DashboardPage");
export const importLogin = () => import("@/pages/Login");
export const importSignupPage = () => import("@/pages/SignupPage");
export const importNotFoundPage = () => import("@/pages/NotFoundPage");

// Lazy load all major pages with retry
export const LazyIndex = retryLazy(importIndex);
export const LazyPersonaSelection = retryLazy(importPersonaSelection);
export const LazyDashboardPage = retryLazy(importDashboardPage);
export const LazyLogin = retryLazy(importLogin);
export const LazySignupPage = retryLazy(importSignupPage);
export const LazyNotFoundPage = retryLazy(importNotFoundPage);

/**
 * Layout-preserving skeleton — shown only when a chunk genuinely isn't cached
 * yet. Avoids the hard "blocking spinner" feel and gives the user instant
 * structure. Light/warm theme per project memory; no framer-motion.
 */
export const RouteSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background" aria-busy="true" aria-live="polite">
    {/* Header bar placeholder — keeps the visual frame steady */}
    <div className="h-14 border-b border-border/60 bg-card/40 flex items-center px-4 sm:px-6 gap-3">
      <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
      <div className="h-4 w-32 rounded bg-muted animate-pulse" />
      <div className="ml-auto h-8 w-20 rounded bg-muted animate-pulse" />
    </div>
    {/* Content blocks */}
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto">
      <div className="h-7 w-2/3 sm:w-1/3 rounded bg-muted animate-pulse" />
      <div className="h-4 w-full sm:w-1/2 rounded bg-muted/70 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="h-32 rounded-xl bg-muted/60 animate-pulse" />
        <div className="h-32 rounded-xl bg-muted/60 animate-pulse" />
      </div>
      <div className="h-48 rounded-xl bg-muted/50 animate-pulse" />
    </div>
    <span className="sr-only">Loading…</span>
  </div>
);

// Enhanced lazy wrapper — skeleton fallback + 8s slow-network escape hatch.
export const LazyWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showSlowMessage, setShowSlowMessage] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setShowSlowMessage(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Suspense
      fallback={
        <>
          <RouteSkeleton />
          {showSlowMessage && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-card border border-border rounded-full shadow-lg px-4 py-2 flex items-center gap-3">
              <p className="text-muted-foreground text-sm">Still loading…</p>
              <Button variant="outline" size="sm" onClick={() => window.location.replace(window.location.origin + '?hard_reset=1')}>
                Reload
              </Button>
            </div>
          )}
        </>
      }
    >
      {children}
    </Suspense>
  );
};

// Route-specific loading states
export const DashboardLoader = () => <LoadingCard variant="dashboard" />;
export const AuthLoader = () => <LoadingCard variant="card" />;
export const DataLoader = () => <LoadingCard variant="table" />;
